/**
 * Block 13 PR-G Phase 6.5G A8 (2026-05-25) — Bottom-sheet выбора режима
 * создания нового spec block либо нового draft Договора.
 *
 * Используется в ДВУХ местах (consolidated UI choice):
 *   1. **`ContractListContainer` (entry с Главной)** — кнопка «Создать договор»
 *      открывает sheet. «Взять данные из КП» → chain в ChoiceSheetForQuotes →
 *      navigate `/contracts/new?fromQuote=<uuid>`. «Заполнить вручную» →
 *      navigate `/contracts/new?manual=true`.
 *   2. **`SectionSpec` («+ Добавить мебель» внутри Договора)** — кнопка
 *      открывает sheet. «Взять данные из КП» → chain в ChoiceSheetForQuotes →
 *      dispatch `ADD_SPEC_BLOCK { seedValues, customRows }`. «Заполнить вручную»
 *      → dispatch `ADD_SPEC_BLOCK { manualSeed: true }` (17 default позиций).
 *
 * Pure UI компонент — никакой fetch logic (chain в ChoiceSheetForQuotes делает
 * fetch quotes). Pattern mirror `ChoiceSheetForQuotes` (backdrop click, Escape
 * close, vertical button stack per P-019).
 *
 * **A11y:** modal dialog pattern; backdrop click closes; Escape closes;
 * button click dispatches handler then closes.
 *
 * Reference:
 *   - ADR-0025 Amendment A8.5 (canonical pattern)
 *   - `ChoiceSheetForQuotes.tsx` (sibling)
 */

import { useEffect } from 'react';
import { X } from 'lucide-react';

export interface ChoiceSheetCreateModeProps {
  isOpen: boolean;
  onClose: () => void;
  /** «Взять данные из КП» — caller обычно chain-открывает ChoiceSheetForQuotes. */
  onSelectFromQuote: () => void;
  /**
   * «Заполнить вручную» — caller либо navigate `?manual=true`, либо dispatch
   * `ADD_SPEC_BLOCK { manualSeed: true }`.
   */
  onSelectManual: () => void;
  /** Title — варьируется per use-case: «Создание договора», «Добавить мебель». */
  title?: string;
  /** Subtitle описание action. */
  subtitle?: string;
}

const BUTTON_CLASS =
  'w-full min-h-tap rounded-xl border-[1.5px] border-accent bg-card text-ink text-base font-semibold ' +
  'px-4 py-3 text-left focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90';

export function ChoiceSheetCreateMode({
  isOpen,
  onClose,
  onSelectFromQuote,
  onSelectManual,
  title = 'Создание договора',
  subtitle,
}: ChoiceSheetCreateModeProps) {
  useEffect(() => {
    if (!isOpen) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  function handleFromQuote() {
    onSelectFromQuote();
    onClose();
  }

  function handleManual() {
    onSelectManual();
    onClose();
  }

  return (
    <div
      onClick={onClose}
      role="presentation"
      data-testid="choice-create-mode-backdrop"
      className="fixed inset-0 z-50 bg-black/50 flex flex-col justify-end items-center"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="choice-create-mode-title"
        data-testid="choice-create-mode-sheet"
        className="w-full max-w-md bg-canvas rounded-t-3xl px-4 pt-3 pb-8"
      >
        <div className="flex items-center justify-between mb-2">
          <h2 id="choice-create-mode-title" className="font-bold text-ink text-xl m-0">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Закрыть"
            data-testid="choice-create-mode-close"
            className="w-11 h-11 flex items-center justify-center rounded-md text-ink focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-70"
          >
            <X size={20} aria-hidden="true" />
          </button>
        </div>
        {subtitle ? (
          <p className="text-sm text-ink/60 mb-3" data-testid="choice-create-mode-subtitle">
            {subtitle}
          </p>
        ) : null}

        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={handleFromQuote}
            aria-label="Взять данные из КП"
            data-testid="choice-create-mode-from-quote"
            className={BUTTON_CLASS}
          >
            Взять данные из КП
          </button>
          <button
            type="button"
            onClick={handleManual}
            aria-label="Заполнить вручную"
            data-testid="choice-create-mode-manual"
            className={BUTTON_CLASS}
          >
            Заполнить вручную
          </button>
        </div>
      </div>
    </div>
  );
}
